import { gql } from "@apollo/client";
import { ApiService } from "./ApiService";
import { OpResult } from "@sdflc/api-helpers";
import { Display } from "./DisplayService";
import { Venue } from "./VenueService";
import { Event } from "./EventService";

// GraphQL query
const DISPLAY_SCHEDULE_QUERY = gql`
  query DisplaySchedule($filter: DisplayScheduleFilter!) {
    displaySchedule(filter: $filter) {
      code
      data {
        display {
          id
          venueId
          layoutId
          name
          description
          blockTransitionAnimation
          status
          displayZones {
            displayZoneId
            displayZoneType
            displayZoneTitle
            playListId
            fallbackPlayListId
            scheduleType
            venueId
            roomId
            roomTypeId
            floor
            roomCategoryId
            eventCategoryId
            showPastHours
            showUpcomingHours
            playList {
              id
              name
              blockTransitionAnimation
              progressStyle
              playListItems {
                id
                contentBlockId
                durationSeconds
                startDatetime
                endDatetime
                startTime
                endTime
                recurrencePattern
                recurrenceDays
                orderNo
                status
                contentBlock {
                  id
                  name
                  contentType
                  contentData
                  fileUrl
                  durationSeconds
                  startDatetime
                  endDatetime
                  startTime
                  endTime
                  recurrencePattern
                  recurrenceDays
                  blockSettings
                  orderNo
                }
              }
            }
            fallbackPlayList {
              id
              name
              blockTransitionAnimation
              progressStyle
              playListItems {
                id
                contentBlockId
                durationSeconds
                startDatetime
                endDatetime
                startTime
                endTime
                recurrencePattern
                recurrenceDays
                orderNo
                status
                contentBlock {
                  id
                  name
                  contentType
                  contentData
                  fileUrl
                  durationSeconds
                  startDatetime
                  endDatetime
                  startTime
                  endTime
                  recurrencePattern
                  recurrenceDays
                  blockSettings
                  orderNo
                }
              }
            }
            room {
              roomTypeId
              name
              roomNumber
              description
              capacity
              footageSqm
              floor
              locationNotes
            }
          }
          displayRunningMessages {
            runningMessageId
            startDatetime
            endDatetime
            orderNo
            runningMessage {
              messageSourceType
              messageText
              scrollSpeed
              startDatetime
              endDatetime
              startTime
              endTime
              recurrencePattern
              recurrenceDays
              importance
              status
            }
          }
          emergencyContentBlock {
            id
            name
            contentType
            contentData
            fileUrl
            startDatetime
            endDatetime
            startTime
            endTime
            recurrencePattern
            recurrenceDays
            blockSettings
          }
          theme {
            name
            themeConfig
          }
          layout {
            name
            layoutConfig
          }
        }
        venue {
          id
          name
          logoUrl
          description
          city
          country
          timezone
          status
        }
        events {
          id
          venueId
          recurringEventId
          clientId
          roomId
          name
          description
          eventDate
          startTime
          endTime
          color
          recurrencePattern
          daysOfWeek
          displayZoneId
          imageUploadedFileId
          externalUrlType
          externalUrl
          status
          rooms {
            id
            roomTypeId
            name
            roomNumber
            tags
            description
            capacity
            footageSqm
            floor
            locationNotes
            categories {
              id
              name
            }
          }
          eventTeams {
            teamName
            supportingRooms {
              id
              roomTypeId
              name
              roomNumber
              description
              capacity
              footageSqm
              floor
              locationNotes
            }
          }
          categories {
            id
            name
          }
        }
        totalEvents
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

export interface ScheduleEvent extends Event {
  isPast?: boolean;
}

export interface DisplayScheduleData {
  display?: Display;
  venue: Venue;
  events: Event[];
  totalEvents: number;
  dateRange: DisplayScheduleDateRange;
}

export interface DisplayScheduleDateRange {
  startDate: string;
  endDate: string;
  requestedDate?: string;
  requestedStartDate?: string;
  requestedEndDate?: string;
}

export interface DisplayScheduleFilter {
  displayId?: string | null | undefined;
  venueId?: string | null | undefined;
  date?: string | null | undefined;
  startDate?: string | null | undefined;
  endDate?: string | null | undefined;
  roomIds?: string[] | null | undefined;
  roomTypeIds?: string[] | null | undefined;
  floor?: string | null | undefined;
  clientIds?: string[] | null | undefined;
  includeOverrides?: boolean | null | undefined;
  preview?: boolean | null | undefined;
  appVersion?: string | null | undefined;
}

/**
 * Display Schedule service that handles display schedule-related API calls
 * Provides schedule data for displays including events and date ranges
 */
export class DisplayScheduleService {
  /**
   * Get display schedule data with filtering options
   */
  static async getSchedule(filter: DisplayScheduleFilter): Promise<OpResult> {
    const result = await ApiService.executeQuery(DISPLAY_SCHEDULE_QUERY, {
      filter,
    });
    return result;
  }
}
